import { ProgressStepperConfig } from './activity.model';
import { CostData } from './asset-details.model';
import { FieldGroupMetaData } from './checkout.model';
import { CountryItem, OrderActionType } from './dashboard.model';
import { CartFeatureItem } from './manage-features.model';
import { ShoppingCartData } from './shopping-cart.model';

// ###############
// ### Devices ###
// ###############
export interface Carrier_Type {
  id: number | string;
  image_url: string;
  name: string;
}

export interface DeviceData {
  items: DeviceItemData[] | DeviceItemDataBP[];
  filters: ShoppingFilterData[];
  filter_tokens: FilterToken[];
  category: ShoppingCategoryData[];
  progress_bar_config: ProgressStepperConfig;
  in_cart_device: DeviceItemDevice;
  admin_options?: string | undefined;
  is_device_list_business_purpose_response?: boolean | undefined;
}

export interface DeviceItemData {
  model: string;
  carrier: DeviceItemCarrier;
  manufacturer: string;
  admin: boolean;
  onetime_minimum_price: string;
  onetime_minimum_price_fractional: number;
  monthly_minimum_price: string;
  monthly_minimum_price_fractional: number;
  total: number;
  devices: DeviceItemDevice[];
  in_cart?: boolean;
}

export interface DeviceItemCarrier {
  id: number;
  name: string;
  image_url: string;
  admin?: boolean;
}

export interface ListResponse<T> {
  items: T[];
}

export type WithSelected<T> = T & { selected?: boolean };

export interface DeviceCarriersListResponse {
  carriers: DeviceItemCarrier[];
}

export type CarriersListResponse = ListResponse<WithSelected<DeviceItemDevice>>;

export type ColorsListResponse = ListResponse<WithSelected<DeviceItemDevice>>;

export type ColorOption = WithSelected<DeviceItemDevice>;

export type StorageListResponse = ListResponse<DeviceItemDevice>;

export type NameOverrideListResponse = ListResponse<DeviceItemDevice>;

export interface DeviceItemDevice {
  id: number;
  name: string;
  description: string;
  model: string;
  price: DevicePrice;
  carrier_id: number;
  platform: string;
  sku: string;
  consumer_sku: string;
  device_catalog_id: number;
  product_category: string;
  manufacturer: string;
  wireless_capabilities: string;
  manufacturer_sku: string;
  device_compatibility: string;
  image_url: string;
  color: string;
  original_color: string;
  size: string;
  name_override: string;
  admin?: boolean;
}

export interface DeviceItemDataBP {
  onetime_minimum_price: string;
  onetime_minimum_price_fractional: number;
  monthly_minimum_price: string;
  monthly_minimum_price_fractional: number;
  devices: DeviceItemDeviceBP[];
  in_cart?: boolean;
}

export interface DeviceItemDeviceBP extends DeviceItemDevice {
  carrier: DeviceItemCarrier;
  business_purpose: string;
  in_cart?: boolean;
}

export interface ManageBusinessPlans {
  plans: PlanType[];
  progress_bar_config?: ProgressStepperConfig;
}

export interface ManageAddOnFeatures {
  features: FeatureType[];
  progress_bar_config?: ProgressStepperConfig;
}

export interface PlanType {
  carrier: Carrier_Type;
  cost_to_currency: string;
  data_allowance: string;
  description: string;
  full_description: string;
  id: number;
  name: string;
  name_override: string;
  plan_id: number;
  plan_usage: string;
  features: FeatureType[];
  admin?: boolean;
}

export interface FeatureType {
  id: number;
  name: string;
  category: string;
  description: string;
  cost_to_currency: string;
  name_override: string;
  feature_id: number;
  included: boolean;
  unchanged: boolean;
  cost?: CostData;
  admin?: boolean;
}

export interface DevicePrice {
  monthly: CostToCurrency;
  onetime: CostToCurrency;
}

export interface CostToCurrency {
  cost_to_currency: string;
  fractional?: number;
}

// ###################
// ### Accessories ###
// ###################
export interface AccessoriesData {
  accessories: AccessoryItemData[];
  filters: ShoppingFilterData[];
  filter_tokens: FilterToken[];
  category: ShoppingCategoryData[];
  progress_bar_config: ProgressStepperConfig;
  cart_updated?: boolean;
}

export interface AccessoryItemData {
  id: number | string;
  name: string;
  active: boolean;
  category: string;
  category_id: number;
  description: string;
  cost_to_currency: string;
  cost_fractional: number;
  image_url: string;
  manufacturer?: string; // TODO this will not be optional at some point
  color?: string;
  vendor: AccessoryCarrierInfo;
  admin?: boolean;
  in_cart?: boolean; // used only in FE, not retrieved from BE
  quantity?: number; // used only in FE, not retrieved from BE
}

export interface AccessoryCarrierInfo {
  id: number;
  name: string;
}

// #######################
// ### Order Approvers ###
// #######################
export interface OrderApproversResponse {
  approvers: OrderApproverData[];
  notification: string;
  basic: boolean;
}

export interface OrderApproverData {
  id: number;
  type: string;
  title: string;
  allow_selection: boolean;
  approver_group_id?: number;
  approver?: OrderApprover;
}

export interface OrderApprover {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
}

export interface OrderApproversLookupData {
  meta: OrderApproverLookupMetaData;
  items: OrderApproversLookupItem[];
}

export interface OrderApproverLookupMetaData {
  total_count: number;
  limit: number;
  offset: number;
  page_number: number;
  records_per_page: number;
  total_pages: number;
}

export interface OrderApproversLookupItem {
  id: number;
  person_name: string;
  email: string;
  business_id: number;
  cost_center: string;
  employee_id: string;
  status: boolean;
  country_location: string;
  group_name: string;
  supervisor: string;
  status_display_name: string;
}

export interface SelectedOrderApprover {
  id: number;
  user_id: string;
  email: string;
}

// #################################################
// ### Shared (filters/categories, translations) ###
// #################################################
export interface TranslationBundle {
  name: string;
  value: string;
}

export interface StoreQueryParams {
  carrierId: number;
  name: string | null;
  filterConfigId: number;
  isBpDevice?: boolean;
  filters?: string;
  categories: string;
  countryId: number;
  deviceId: number;
  number_of_plans: number;
  number_of_features: number;
}

export interface ShoppingFilterData {
  items: ShoppingFilterItem[];
  config: ShoppingCategoryConfig;
}

export interface ShoppingFilterItem {
  item?: string;
  active?: boolean;
  id?: number | string;
  name?: string;
  value?: string;
  filter_name?: string;
}

export interface FilterToken {
  id: number;
  value: string;
}

export interface ShoppingCategoryData {
  items: ShoppingCategoryItem[];
  config: ShoppingCategoryConfig;
}

export interface DeviceIdentifier {
  imei: string;
  serial_number: string;
}

export interface ShoppingCategoryItem {
  item: string;
  active?: boolean;
  material_icon_class: string;
  display_name: string;
  default_filter?: boolean;
  other_group_items: string[];
}

export interface ShoppingCategoryConfig {
  filter_config_id: number;
  client_id: number;
  page_id: string;
  order_type_id: number;
  product_id: number;
  filter_name: string;
  filter_display_name: string;
  filter_column_name: string;
  category: boolean;
}

export interface ShoppingDataInitialLoad {
  categories: { id: number; value: string }[];
  filters: ShoppingFilterItem[];
  orderTypeId: number | null;
  deviceId: number | string | null;
  refDeviceId?: number | string | null;
  lineId?: number | string | null;
  keep_plan?: boolean;
  filter_tokens?: ShoppingFilterItem[];
  include_admin_options?: boolean;
}

export interface ShoppingSortData {
  field: string;
  dir: string;
}

export interface ShoppingOrderConfig {
  type: OrderActionType & { name: string };
  country?: CountryItem;
  device_catalog_id?: number;
  device_type?: string;
  lineId?: number;
  carrier_id?: number;
  keep_plan?: boolean;
  excludeCarrierId?: number;
}

export interface DeviceCreationFormFiltersConfig {
  category: number;
  brand: number;
  model: number;
  color: number;
  storage: number;
}

export type DeviceCreationFormDeviceCharacteristics = Record<
  keyof DeviceCreationFormFiltersConfig,
  { id: string; name: string }[]
>;

export interface OrderTypeSettings {
  can_keep_plan?: boolean;
  require_cold_device?: boolean;
  accessory_ordering_enabled: boolean;
  bulk_quantity_min_limit?: number;
  bulk_quantity_max_limit?: number;
  restrict_transfer_to_another_carrier?: boolean;
}

export interface ClientSettings {
  notify_user_of_etf: boolean;
  cc_settings_enabled: boolean;
  enable_pendo?: boolean;
}

export interface ReviewFields {
  progress_bar_config: ProgressStepperConfig;
  groups: FieldGroupMetaData[];
}

export interface ValidateFields {
  valid: boolean;
  error_messages?: FieldErrorMessage[];
}

export interface FieldErrorMessage {
  id: string;
  field_id: string;
  error_message: string[];
}

export interface SimFormOutputEvent {
  valid: boolean;
  type: string;
  value: string | undefined;
  identifier: string | undefined;
}

export interface PreFlight {
  orderTypeId: number;
  lineId: number;
  country_id: number;
}

export interface HelpTextsInfo {
  device_identifier_help_text: string;
}

// #################
// ### Constants ###
// #################
export const DEVICE_LIST_SORT_VALUES: string[] = [
  'Price: Low to High',
  'Price: High to Low',
  'Brand: A to Z',
  'Brand: Z to A',
  'Carrier: A to Z',
  'Carrier: Z to A'
];

export const ACCESSORY_LIST_SORT_VALUES: string[] = [
  'Price: Low to High',
  'Price: High to Low',
  'Name: A to Z',
  'Name: Z to A'
];

export const SIM_OPTION_LABELS: string[] = [
  'Activate a SIM card I already own',
  'Send me a new SIM card',
  'Activate on eSIM'
];

export const CATEGORY_FILTER_SORT_CLASSES: string =
  'col d-flex justify-content-start justify-content-md-between flex-column flex-md-row px-0 border-bottom category-filter-sort';

// TODO: figure out how to get these implemented w/o adding blank items to cart (/price)
export const SHOPPING_CART_INITIAL_LOAD: ShoppingCartData = {
  shopping_cart_items: [],
  orderTypeId: 0,
  lineId: undefined
};

export const SHOPPING_CART_INITIAL_LOAD_LINEID_ZERO: ShoppingCartData = {
  shopping_cart_items: [],
  orderTypeId: 0,
  lineId: 0
};

export const SHOPPING_CART_INITIAL_LOAD_NO_LINE: ShoppingCartData = {
  shopping_cart_items: [],
  orderTypeId: 0
};

export const ACTIVITY_3_ASSET_CARD_FIELDS: string[] = [
  'eid',
  'imei',
  'imei2',
  'serial_number',
  'sim_compatibility'
];

export const CART_ITEM_TYPE_DEVICE: string = 'DEVICE';
export const CART_ITEM_TYPE_PLAN: string = 'PLAN';
export const CART_ITEM_TYPE_FEATURE: string = 'FEATURE';
export const CART_ITEM_TYPE_ACCESSORY: string = 'ACCESSORY';
export const REMOVED_FEATURE: CartFeatureItem['sub_type'] = 'REMOVED_FEATURE';
export const ADDED_FEATURE: CartFeatureItem['sub_type'] = 'ADDED_FEATURE';
export const UNCHANGED_FEATURE: CartFeatureItem['sub_type'] = 'UNCHANGED_FEATURE';

export enum ADMIN_OPTIONS {
  ALL_ADMIN_OPTIONS = 'ALL_ADMIN_OPTIONS',
  ALL_NON_ADMIN_OPTIONS = 'ALL_NON_ADMIN_OPTIONS',
  ADMIN_NON_ADMIN_OPTIONS = 'ADMIN_NON_ADMIN_OPTIONS'
}
